/**
 * 🎟️ PROMO CODES MANAGEMENT PAGE
 * Admin dashboard for creating and managing discount codes
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/promo-codes
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  LocalOffer as OfferIcon,
  CheckCircle as ActiveIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-hot-toast';
import promoCodeService, {
  PromoCode,
  PromoCodeStats,
  CodeType,
  DiscountType,
} from '@/services/promoCodeService';

export const PromoCodesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [stats, setStats] = useState<PromoCodeStats | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCodes, setTotalCodes] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<CodeType | 'all'>('all');
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);

  // Create/Edit Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState<Partial<PromoCode>>({
    code: '',
    name: '',
    description: '',
    type: 'custom',
    discountType: 'percentage',
    discountValue: 0,
    isActive: true,
    validFrom: new Date().toISOString(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  });

  // Bulk Generate Dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkConfig, setBulkConfig] = useState({
    quantity: 10,
    prefix: 'PROMO',
    name: 'Bulk Generated Codes',
    discountType: 'percentage' as DiscountType,
    discountValue: 10,
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  });

  useEffect(() => {
    loadCodes();
    loadStats();
  }, [page, rowsPerPage, filterType, filterActive, searchQuery]);

  const loadCodes = async () => {
    try {
      setLoading(true);

      const result = await promoCodeService.getCodes({
        page: page + 1,
        limit: rowsPerPage,
        type: filterType,
        isActive: filterActive,
        search: searchQuery || undefined,
      });


      if (result.success && result.data) {
        setCodes(result.data.codes || []);
        setTotalCodes(result.data.pagination?.total || 0);
      } else {
        console.error('❌ Failed to load codes:', result.error);
        setCodes([]);
        setTotalCodes(0);
        if (result.error) {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('❌ Error loading codes:', error);
      setCodes([]);
      setTotalCodes(0);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await promoCodeService.getStats();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingCode(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      type: 'custom',
      discountType: 'percentage',
      discountValue: 0,
      isActive: true,
      validFrom: new Date().toISOString(),
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    });
    setDialogOpen(true);
  };

  const handleEdit = (code: PromoCode) => {
    setEditingCode(code);
    setFormData(code);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.discountValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      const result = editingCode
        ? await promoCodeService.updateCode(editingCode.id, formData)
        : await promoCodeService.createCode(formData);


      if (result.success) {
        toast.success(result.message || `Promo code ${editingCode ? 'updated' : 'created'} successfully!`);
        setDialogOpen(false);

        // Force immediate refresh
        await loadCodes();
        await loadStats();
      } else {
        console.error('❌ Save failed:', result.error);
        toast.error(result.error || 'Failed to save promo code');
      }
    } catch (error) {
      console.error('❌ Error saving code:', error);
      toast.error('Failed to save promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (code: PromoCode) => {
    if (!confirm(`Delete promo code "${code.code}"?`)) return;

    try {
      setLoading(true);
      const result = await promoCodeService.deleteCode(code.id);

      if (result.success) {
        toast.success('Promo code deleted successfully');
        loadCodes();
        loadStats();
      } else {
        toast.error(result.error || 'Failed to delete promo code');
      }
    } catch (error) {
      console.error('Error deleting code:', error);
      toast.error('Failed to delete promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (bulkConfig.quantity < 1 || bulkConfig.quantity > 1000) {
      toast.error('Quantity must be between 1 and 1000');
      return;
    }

    try {
      setLoading(true);
      const result = await promoCodeService.bulkGenerateCodes({
        quantity: bulkConfig.quantity,
        prefix: bulkConfig.prefix,
        name: bulkConfig.name,
        discountType: bulkConfig.discountType,
        discountValue: bulkConfig.discountValue,
        validUntil: bulkConfig.validUntil,
        type: 'custom',
      });

      if (result.success) {
        toast.success(`Generated ${result.data?.count || bulkConfig.quantity} promo codes!`);
        setBulkDialogOpen(false);
        loadCodes();
        loadStats();
      } else {
        toast.error(result.error || 'Failed to generate codes');
      }
    } catch (error) {
      console.error('Error generating codes:', error);
      toast.error('Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copied to clipboard!');
  };

  const formatDiscount = (code: PromoCode) => {
    if (code.discountType === 'percentage') {
      return `${code.discountValue}%`;
    }
    return `NGN ${code.discountValue.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Promo Codes Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage discount codes for bookings
          </Typography>
        </Box>

        {/* Statistics Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <OfferIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.overview?.totalCodes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Codes
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <ActiveIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.overview?.activeCodes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Codes
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TrendingIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.overview?.totalRedemptions || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Uses
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <MoneyIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.discounts?.currency || 'NGN'} {(stats.discounts?.totalDiscountGiven || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Discounts
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters and Actions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  label="Type"
                  onChange={(e) => setFilterType(e.target.value as CodeType | 'all')}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="seasonal">Seasonal</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                  <MenuItem value="partner">Partner</MenuItem>
                  <MenuItem value="first-rider">First Rider</MenuItem>
                  <MenuItem value="custom">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterActive === undefined ? 'all' : filterActive ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilterActive(val === 'all' ? undefined : val === 'active');
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadCodes}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setBulkDialogOpen(true)}
                >
                  Bulk Generate
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                >
                  Create Code
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Codes Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (!codes || codes.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !codes || codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        No promo codes found. Create your first code!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  codes.map((code) => (
                    <TableRow key={code.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={code.code} color="primary" size="small" />
                          <IconButton size="small" onClick={() => copyToClipboard(code.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {code.name}
                        </Typography>
                        {code.description && (
                          <Typography variant="caption" color="text.secondary">
                            {code.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={code.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDiscount(code)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {code.currentRedemptions}
                          {code.maxRedemptions ? ` / ${code.maxRedemptions}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(code.validUntil)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={code.isActive ? 'Active' : 'Inactive'}
                          color={code.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(code)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(code)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCodes}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCode ? 'Edit Promo Code' : 'Create New Promo Code'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  placeholder="SUMMER2024"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={!!editingCode}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type || 'custom'}
                    label="Type"
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as CodeType })}
                  >
                    <MenuItem value="seasonal">Seasonal</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="partner">Partner</MenuItem>
                    <MenuItem value="first-rider">First Rider</MenuItem>
                    <MenuItem value="custom">General</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name *"
                  placeholder="Summer Sale 2024"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="20% off all bookings this summer"
                  multiline
                  rows={2}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={formData.discountType || 'percentage'}
                    label="Discount Type"
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as DiscountType })}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Discount Value *"
                  type="number"
                  value={formData.discountValue || ''}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  InputProps={{
                    endAdornment: formData.discountType === 'percentage' ? '%' : 'NGN',
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Redemptions"
                  type="number"
                  placeholder="Leave empty for unlimited"
                  value={formData.maxRedemptions || ''}
                  onChange={(e) => setFormData({ ...formData, maxRedemptions: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Per User"
                  type="number"
                  placeholder="Leave empty for unlimited"
                  value={formData.maxPerUser || ''}
                  onChange={(e) => setFormData({ ...formData, maxPerUser: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Valid From"
                  value={formData.validFrom ? new Date(formData.validFrom) : new Date()}
                  onChange={(date) => setFormData({ ...formData, validFrom: date?.toISOString() || new Date().toISOString() })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Valid Until"
                  value={formData.validUntil ? new Date(formData.validUntil) : new Date()}
                  onChange={(date) => setFormData({ ...formData, validUntil: date?.toISOString() || new Date().toISOString() })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive ?? true}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={loading}>
              {loading ? 'Saving...' : editingCode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Generate Dialog */}
        <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Bulk Generate Promo Codes</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={bulkConfig.quantity}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, quantity: Number(e.target.value) })}
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Code Prefix"
                  value={bulkConfig.prefix}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, prefix: e.target.value.toUpperCase() })}
                  placeholder="PROMO"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={bulkConfig.name}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, name: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={bulkConfig.discountType}
                    label="Discount Type"
                    onChange={(e) => setBulkConfig({ ...bulkConfig, discountType: e.target.value as DiscountType })}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Discount Value"
                  type="number"
                  value={bulkConfig.discountValue}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, discountValue: Number(e.target.value) })}
                  InputProps={{
                    endAdornment: bulkConfig.discountType === 'percentage' ? '%' : 'NGN',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <DateTimePicker
                  label="Valid Until"
                  value={new Date(bulkConfig.validUntil)}
                  onChange={(date) => setBulkConfig({ ...bulkConfig, validUntil: date?.toISOString() || new Date().toISOString() })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  Codes will be generated with random suffixes (e.g., {bulkConfig.prefix}-A3F7)
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkGenerate} variant="contained" disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PromoCodesPage;
